import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save, ChevronUp, ChevronDown, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage, formatApiErrorDetail } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";

export default function AdminSlides() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | slide

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/slides");
      setSlides(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load slides");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/admin/slides/${form.id}`, form);
      else await api.post("/admin/slides", form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    }
  };

  const toggleVisible = async (s) => {
    try {
      await api.put(`/admin/slides/${s.id}`, { ...s, is_visible: !s.is_visible });
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= slides.length) return;
    const next = [...slides];
    [next[idx], next[target]] = [next[target], next[idx]];
    setSlides(next);
    try {
      await Promise.all(next.map((s, i) => s.sort_order === i ? null : api.put(`/admin/slides/${s.id}`, { ...s, sort_order: i })));
      load();
    } catch { load(); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this slide? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/slides/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-slides-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link to="/admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8">
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <div className="border-b border-rule pb-6 mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-3">Slideshow</p>
            <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">Home page slideshow</h1>
            <p className="text-sm text-ink-soft mt-2 max-w-xl">
              Upload photos to display near the top of your home page.
              Toggle &quot;Show on site&quot; on/off to swap galleries in and out — old ones stay saved for later reuse.
            </p>
          </div>
          {!editing && (
            <button onClick={() => setEditing({ image_url: "", caption: "", is_visible: true, sort_order: slides.length })}
              data-testid="slides-new-btn"
              className="inline-flex items-center gap-2 px-5 py-3 bg-ink text-paper text-xs tracking-[0.2em] uppercase hover:bg-terracotta transition-colors">
              <Plus size={14} /> Add slide
            </button>
          )}
        </div>

        {editing && <SlideForm initial={editing} onCancel={() => setEditing(null)} onSave={save} />}

        {loading ? (
          <p className="text-ink-soft">Loading…</p>
        ) : !editing && slides.length === 0 ? (
          <p className="italic text-ink-soft">No slides yet. Click &quot;Add slide&quot; to upload your first photo.</p>
        ) : !editing && (
          <ul className="divide-y divide-rule">
            {slides.map((s, i) => (
              <li key={s.id} className="py-4 flex items-center gap-3">
                <img src={resolveImage(s.image_url)} alt="" className="w-20 h-14 object-cover border border-rule shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-ink truncate">{s.caption || <span className="italic text-ink-soft">No caption</span>}</p>
                  {!s.is_visible && <p className="text-[10px] text-ink-soft uppercase tracking-wider mt-1">Hidden — archived</p>}
                </div>
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 border border-rule text-ink-soft hover:text-ink hover:border-ink disabled:opacity-30" title="Move up"><ChevronUp size={14} /></button>
                <button onClick={() => move(i, 1)} disabled={i === slides.length - 1} className="p-2 border border-rule text-ink-soft hover:text-ink hover:border-ink disabled:opacity-30" title="Move down"><ChevronDown size={14} /></button>
                <button onClick={() => toggleVisible(s)} className="p-2 border border-rule text-ink-soft hover:text-ink hover:border-ink" title={s.is_visible ? "Hide from site" : "Show on site"}>
                  {s.is_visible ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button onClick={() => setEditing(s)} className="px-3 py-2 border border-rule text-xs uppercase tracking-wider text-ink-soft hover:text-ink hover:border-ink">Edit</button>
                <button onClick={() => remove(s.id)} className="p-2 border border-rule text-ink-soft hover:text-terracotta hover:border-terracotta" title="Delete"><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SlideForm({ initial, onCancel, onSave }) {
  const [form, setForm] = useState({ ...initial });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.image_url) { toast.error("Please upload a photo first."); return; }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="border border-rule bg-paper-2/30 p-6 mb-8 space-y-5">
      <ImageUploader value={form.image_url} onChange={(v) => update("image_url", v)} label="Photo" testIdPrefix="slide-photo" />
      <div>
        <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">Caption (optional)</label>
        <textarea value={form.caption} onChange={(e) => update("caption", e.target.value)} rows={2} maxLength={400}
          className="w-full bg-paper-2/40 border border-rule focus:border-ink p-3 text-sm text-ink outline-none italic"
          placeholder="A short line about this photo…" />
      </div>
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={form.is_visible} onChange={(e) => update("is_visible", e.target.checked)} className="w-4 h-4" />
        <span className="text-sm text-ink">Show on site (uncheck to archive)</span>
      </label>
      <div className="flex gap-3 pt-2 border-t border-rule">
        <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 bg-terracotta text-paper text-xs tracking-[0.2em] uppercase hover:bg-ink transition-colors disabled:opacity-50">
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={onCancel} className="px-6 py-3 border border-ink text-ink text-xs tracking-[0.2em] uppercase hover:bg-ink hover:text-paper transition-colors">Cancel</button>
      </div>
    </div>
  );
}
